import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import CustomTextInput from '../../../components/CustomTextInput';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { get, map } from 'lodash';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSignupHandler } from '../dataHandlers/signupHandler';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import appConfig from '../../../config/appConfig';
import OptionalExternalLink from '../../../components/OptionalExternalLink';

export const createSignupPasswordSchema = (t) =>
  Yup.object().shape({
    email: Yup.string().email(t.invalid_email_format).required(t.email_required),
    name: Yup.string().required(t.name_required).min(3, t.name_min_3).max(25, t.name_max_25),
    password: Yup.string()
      .min(8, t.password_min_8)
      .matches(/[A-Z]/, t.password_uppercase)
      .matches(/[a-z]/, t.password_lowercase)
      .matches(/\d/, t.password_number)
      .matches(/[@$!%*?&]/, t.password_special)
      .required(t.password_required),
    confirmPassword: Yup.string()
      .required(t.confirm_password_required)
      .oneOf([Yup.ref('password')], t.passwords_must_match),
    termsAndCondition: Yup.boolean().oneOf([true], t.accept_terms_required),
  });

const SignupPasswordForm = ({ email }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { users: u, language } = useTranslatedMessages();
  const t = u.signup_password ?? {};
  const v = t.validations ?? {};

  const schema = useMemo(
    () =>
      createSignupPasswordSchema({
        invalid_email_format: v.invalid_email_format ?? 'Invalid email format',
        email_required: v.email_required ?? 'Email is required',
        name_required: v.name_required ?? 'Name is required',
        name_min_3: v.name_min_3 ?? 'Name must be at least 3 characters',
        name_max_25: v.name_max_25 ?? 'Name must be at most 25 characters',
        password_min_8: v.password_min_8 ?? 'Password must be at least 8 characters',
        password_uppercase: v.password_uppercase ?? 'Password must contain at least one uppercase letter',
        password_lowercase: v.password_lowercase ?? 'Password must contain at least one lowercase letter',
        password_number: v.password_number ?? 'Password must contain at least one number',
        password_special:
          v.password_special ?? 'Password must contain at least one special character (@, $, !, %, *, ?, &)',
        password_required: v.password_required ?? 'Password is required',
        confirm_password_required: v.confirm_password_required ?? 'Confirm password is required',
        passwords_must_match: v.passwords_must_match ?? 'Passwords must match',
        accept_terms_required: v.accept_terms_required ?? 'You must accept the terms and conditions',
      }),
    [v],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: email || '',
      termsAndCondition: false,
    },
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  const onSuccess = (data) => {
    setLoading(false);
    // Check if user needs verification
    if (data?.verified === false) {
      navigate('/signup', { replace: true, state: { email: email, stage: 'verification' } });
    } else {
      navigate('/login');
    }
  };

  const { onSubmit, error } = useSignupHandler({ onSuccess });

  useEffect(() => {
    map(error, (value, key) => {
      setError(key, { message: value, type: 'string' });
    });
  }, [setError, error]);

  const handleSave = async (data) => {
    setLoading(true);
    await onSubmit(data);
    setLoading(false);
  };

  const formData = watch();

  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={appConfig.brandLogoPath} alt="logo" width="50%" />
      </Stack>

      <Stack spacing={2}>
        <CustomTextInput name="email" label={t.fields?.email ?? 'Email'} control={control} errors={errors} />
        <CustomTextInput name="name" label={t.fields?.name ?? 'Name'} control={control} errors={errors} />
        <CustomTextInput
          name="password"
          type={showPassword ? 'text' : 'password'}
          label={t.fields?.password ?? 'Password'}
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <CustomTextInput
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          label={t.fields?.confirm_password ?? 'Confirm Password'}
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Grid container spacing={0}>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: formData.password?.length >= 8 ? 'green' : '#ff0000' }}>
              {t.password_rules?.min_8 ?? 'Minimum 8 characters.'}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[A-Z]/.test(formData.password) ? 'green' : '#ff0000' }}>
              {t.password_rules?.uppercase ?? 'At least 1 uppercase letter.'}
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[0-9]/.test(formData.password) ? 'green' : '#ff0000' }}>
              {t.password_rules?.number ?? 'At least 1 number.'}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography
              variant="caption"
              sx={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'green' : '#ff0000' }}
            >
              {t.password_rules?.special ?? 'At least one special character.'}
            </Typography>
          </Grid>
        </Grid>
        <Controller
          name="termsAndCondition"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  color="primary"
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                  }}
                />
              }
              label={
                <Box>
                  {t.terms?.prefix ?? 'I agree to the'}{' '}
                  <OptionalExternalLink href={appConfig.termsUrl}>
                    {t.terms?.terms_and_conditions ?? 'terms & conditions'}
                  </OptionalExternalLink>{' '}
                  {t.terms?.and ?? 'and'}{' '}
                  <OptionalExternalLink href={appConfig.privacyUrl}>
                    {t.terms?.privacy_policy ?? 'privacy policy'}
                  </OptionalExternalLink>{' '}
                  {(t.terms?.suffix ?? 'of {brand} platform.').replace('{brand}', appConfig.brandName)}
                </Box>
              }
            />
          )}
        />
        {errors.termsAndCondition && (
          <FormHelperText error>{get(errors, 'termsAndCondition.message', '')}</FormHelperText>
        )}
        <Button type="button" variant="contained" className="submit" onClick={handleSubmit(handleSave)}>
          {loading ? (t.actions?.signing_up ?? 'Signing up...') : (t.actions?.sign_up ?? 'Sign Up')}
        </Button>
        <Typography variant="body2">
          <Link to="/login" style={{ textDecoration: 'none' }}>
            {t.links?.already_have_account_sign_in ?? 'Already have an account? Sign In'}
          </Link>
        </Typography>
      </Stack>
    </>
  );
};

export default SignupPasswordForm;
